import { useCallback, useContext, useEffect, useState } from "react"
import { Navigate, useNavigate, useParams } from "react-router-dom"
import { useHashTab } from "../../../../hooks/useHashTab"
import { digitalManagerApi } from "../../../../services/api"
import { DiMaDetailsHeaderProps } from "../../../components/DiMaDetailsHeader"
import { DiMaProgress } from "../../../components/DiMaProgress"
import { DetailsHeaderTabLayout } from "../../../components/Helpers"
import { UserContext } from "../../../context/UserContext"
import { ApplicationGet } from "../../../models/Application"
import { ApplicationDeviceGet } from "../../../models/ApplicationDevice"
import { ApplicationDetails } from "./details_tab/ApplicationDetails"
import { ApplicationDevices } from "./devices_tab/ApplicationDevices"
import { ApplicationKeyValuePairs } from "./key_value_pairs_tab/ApplicationKeyValuePairs"
import { ApplicationPermissions } from "./permissions_tab/ApplicationPermissions"
import { ApplicationProperties } from "./properties_tab/ApplicationProperties"
import { ApplicationRoles } from "./roles_tab/ApplicationRoles"
import { ApplicationUsers } from "./users_tab/ApplicationUsers"

const returnURL = "/applications"

export function ApplicationContainer() {
  const { applicationId } = useParams()
  const { user, isLoadingUser } = useContext(UserContext)
  const navigate = useNavigate()

  const { activeTab, handleTabChange: handleChange } = useHashTab("details")

  const [app, setApp] = useState<ApplicationGet>()
  const [applicationDevices, setApplicationDevices] = useState<ApplicationDeviceGet>()
  const [reloadEntity, setReloadEntity] = useState<boolean>(true)
  const reloadDevices = useCallback(() => setReloadEntity(true), [])

  useEffect(() => {
    if (applicationId) {
      digitalManagerApi
        .get<ApplicationGet>(`/applications/${applicationId}`)
        .then((res) => setApp(res.data))
        .catch(() => navigate(returnURL))
    }
  }, [applicationId, navigate])

  useEffect(() => {
    if (applicationId && reloadEntity) {
      digitalManagerApi
        .get<ApplicationDeviceGet>(`/applications/${applicationId}/devices`)
        .then((res) => {
          setApplicationDevices(res.data)
          setReloadEntity(false)
        })
        .catch(() => navigate(returnURL))
    }
  }, [applicationId, navigate, reloadEntity])

  if (!applicationId) {
    return <Navigate to={returnURL} />
  }

  if (!app || !applicationDevices || isLoadingUser || !user) {
    return <DiMaProgress />
  }

  const detailsHeaderProps: DiMaDetailsHeaderProps = {
    url: returnURL,
    title: app.name,
    label: "Application ID",
    subtitle: app.id,
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", flexGrow: 1 }}>
      <DetailsHeaderTabLayout
        detailsHeaderProps={detailsHeaderProps}
        tabItems={[
          { id: "details", label: "Details" },
          { id: "roles", label: "Roles" },
          { id: "permissions", label: "Permissions" },
          { id: "properties", label: "Properties" },
          { id: "devices", label: "Devices" },
          { id: "users", label: "Users" },
          { id: "key-value-pairs", label: "Key-Value Pairs" },
        ]}
        activeTab={activeTab}
        onTabChange={handleChange}
      />

      {activeTab === "details" && (
        <ApplicationDetails app={app} user={user} setApplication={setApp} />
      )}
      {activeTab === "roles" && <ApplicationRoles />}
      {activeTab === "permissions" && <ApplicationPermissions />}
      {activeTab === "properties" && <ApplicationProperties />}
      {activeTab === "devices" && (
        <ApplicationDevices
          applicationDevices={applicationDevices}
          reloadDevices={reloadDevices}
        />
      )}
      {activeTab === "users" && <ApplicationUsers />}
      {activeTab === "key-value-pairs" && <ApplicationKeyValuePairs />}
    </div>
  )
}
